import { getServices } from '../../../infra/startup';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import SessionObserver, { SessionEvents } from '../SessionObserver';

/**
 * Event name for native Auth plugin login change events.
 * The event emitted by the JWeb Auth plugin
 * when a user's login status changes.
 */
const AUTH_PLUGIN_LOGIN_CHANGE_EVENT_NAME =
  'com.hp.jarvis.auth.event.login.change';

/**
 * Promise that resolves when login process is completed.
 * This is exposed to allow other modules to await login completion.
 */
export let promiseLoginFinished;

/**
 * Reference to the function that rejects the login promise.
 * Used when the login process times out or encounters an error.
 */
let rejectPromise = undefined;

/**
 * Reference to the function that resolves the login promise.
 * Used when the login process completes successfully.
 */
let resolvePromise = undefined;

/**
 * Stores the timeout ID returned by setTimeout.
 */
let timeoutIdEvent = undefined;

/**
 * Event handler for login completion.
 * This function is triggered when the LOGGEDIN_FINISHED event is fired.
 */
const resolveLoginFinished = () => {
  clearTimeout(timeoutIdEvent);
  internalLogger?.debug('Received clientosLoggedinFinished message event.');

  // Remove the event listener once it's triggered to avoid memory leaks
  SessionObserver.unsubscribe(
    SessionEvents.LOGGEDIN_FINISHED,
    resolveLoginFinished
  );
  resolvePromise(null);
};

/**
 * Sets up a listener for authentication change events.
 * This function initializes the login promise and subscribes to authentication
 * change events. When an auth change event is detected, it starts a timeout
 * to ensure the login process completes in a reasonable time.
 *
 * The sequence is:
 * 1. Initialize login promise via waitLoginToFinish()
 * 2. Subscribe to AUTH_PLUGIN_LOGIN_CHANGE_EVENT_NAME events
 * 3. When event occurs, set timeout to detect stalled login processes
 */
const listenerAuthChangeJWeb = (): void => {
  // Create the promise and set the promiseLoginFinished
  void waitLoginToFinish();

  const timeout = 15000; // 15 seconds
  const { eventService } = getServices();
  // Subscribe to auth change events and set up a timeout to handle potential stalled login
  const authLoginChangeEventUnsubscribePromise = eventService.subscribe(
    AUTH_PLUGIN_LOGIN_CHANGE_EVENT_NAME,
    () => {
      void (async () =>
        (await authLoginChangeEventUnsubscribePromise).unsubscribe())();
      timeoutIdEvent = setTimeout(() => {
        // Clean up event listener to avoid memory leaks
        SessionObserver.unsubscribe(
          SessionEvents.LOGGEDIN_FINISHED,
          resolveLoginFinished
        );
        internalLogger?.warn(
          `Waited ${timeout}ms for login to finish, but timed out.`
        );
        rejectPromise(new Error(`Login process timed out after ${timeout}ms`));

        // Also reload the page
        window.location.reload();
      }, timeout);
    }
  );
};
//

/**
 * Waits for the login process to complete by listening for a LOGGEDIN_FINISHED event.
 *
 * This function creates a Promise that represents the login completion status.
 * It subscribes to the SessionObserver's LOGGEDIN_FINISHED event, which is emitted
 * when the login process completes successfully. The promise will either:
 * - Resolve when the login process completes successfully
 * - Reject if the login process doesn't complete within the specified timeout
 *
 * The promise is stored in the exported 'promiseLoginFinished' variable, allowing
 * other modules to wait for login completion.
 *
 * @returns Promise that resolves when login finishes or rejects on timeout
 */
const waitLoginToFinish = async (): Promise<void> => {
  promiseLoginFinished = new Promise((resolve, reject) => {
    // Store references to the resolve and reject functions for later use
    rejectPromise = reject;
    resolvePromise = resolve;

    // Subscribe to the login finished event
    SessionObserver.subscribe(
      SessionEvents.LOGGEDIN_FINISHED,
      resolveLoginFinished
    );

    internalLogger?.debug(`Waiting for clientosLoggedinFinished event.`);
  });
};

export { waitLoginToFinish, listenerAuthChangeJWeb };
